import React, { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition, faCheck } from "@fortawesome/free-solid-svg-icons";

interface SectionNavItemProps {
  label: string;
  active: boolean;
  onClick: () => void;
  /** FontAwesome icon shown left of the label (section-nav mode). */
  icon?: IconDefinition;
  /** Step number shown in a circle instead of an icon (wizard mode). */
  ordinal?: number;
  /** Marks a wizard step as completed (renders a check inside the circle). */
  done?: boolean;
  disabled?: boolean;
  /** Small extra content rendered right-aligned (e.g. a count). */
  trailing?: React.ReactNode;
  /**
   * Fires once on first hover — lets sections warm their data (fail-silent
   * cache prefetch) before the click. Optional: omitting it changes nothing.
   */
  onPrefetch?: () => void;
}

export function SectionNavItem({
  label,
  active,
  onClick,
  icon,
  ordinal,
  done,
  disabled,
  trailing,
  onPrefetch,
}: SectionNavItemProps) {
  const stateClasses = active
    ? "bg-primary-10 text-primary-100 font-semibold"
    : disabled
    ? "text-gray-400 cursor-not-allowed"
    : "text-gray-600 hover:bg-gray-100";

  const prefetchFired = useRef(false);
  function handleMouseEnter() {
    if (!onPrefetch || prefetchFired.current || disabled) return;
    prefetchFired.current = true;
    onPrefetch();
  }

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onPrefetch ? handleMouseEnter : undefined}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm transition-colors ${stateClasses}`}
    >
      {ordinal !== undefined ? (
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold ${
            done
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : active
              ? "border-primary-100 text-primary-100"
              : "border-gray-200 text-gray-500"
          }`}
        >
          {done ? <FontAwesomeIcon icon={faCheck} /> : ordinal}
        </span>
      ) : (
        icon && (
          <FontAwesomeIcon icon={icon} className="w-4 shrink-0 text-center" />
        )
      )}
      <span className="flex-1 truncate">{label}</span>
      {trailing}
    </button>
  );
}
